import { makeStyles } from "tss-react/mui"
import { DialogContent, Stack } from "@mui/material"
import { ConfigQuery, fetchTimelineResources, TimelineResourcesResponse } from "../../createOrder/createOrderApi"
import { extractAccountingDimensionsWithNames } from "../../createOrder/components/form/helpers"
import { CreateOrderDialogType, ResourceTableSetups, SelectedMainUserState } from "../../createOrder/createOrderTypes"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { DynamicConfigKey, MeetingServiceTabValues } from "../../createOrder/components/form/formTypes"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { StandardButton } from "../../buttons/StandardButton"
import { BillReceiver } from "./components/BillReceiver"
import { OrderSummary } from "./components/OrderSummary"
import { RepeatOrder } from "./components/RepeatOrder"
import { Attendees } from "./components/Attendees"
import { StandardDialog } from "../StandardDialog"
import { Resources } from "./components/Resources"
import { Details } from "./components/Details"
import { StandardLoadingButton } from "../../buttons/LoadingButton"
import { ReceiptDialogTitle } from "./ReceiptDialogTitle"
import { DeliveryNote } from "./components/DeliveryNote"

const useStyles = makeStyles()((theme) => ({
    dialogContent: {
        padding: "3rem",
        paddingTop: "1.5rem",
        display: "flex",
        gap: "4rem",
        flexDirection: "column",
    },
    button: {
        minWidth: "15rem",
    },
    buttonLight: {
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
    },
}))

export type ResourceItem = Readonly<{
    id: number
    title: string
    tableSetups: ResourceTableSetups[]
}>

export const transformTimeResourceData = (data: TimelineResourcesResponse) => {
    const serviceCentersData = data?.serviceCenters

    if (!serviceCentersData) return []

    return serviceCentersData.flatMap((service) =>
        service.resources.map((resource) => ({
            id: resource.id,
            title: resource.displayName || resource.name,
            tableSetups: resource?.room?.tableSetups || [],
        })),
    )
}

type ReceiptConfirmOrderDialogProps = Readonly<{
    open: boolean
    onClose: () => void
    handleConfirm: () => void
    dialogType: CreateOrderDialogType
    orderDetails: MeetingServiceTabValues
    orderId?: number
    mainUser: SelectedMainUserState
    isSubmitting?: boolean
}>

export function ReceiptConfirmOrderDialog({
    open,
    onClose,
    handleConfirm,
    dialogType,
    orderDetails,
    orderId,
    mainUser,
    isSubmitting,
}: ReceiptConfirmOrderDialogProps) {
    const { classes, cx } = useStyles()
    const isRefundOrder = dialogType === CreateOrderDialogType.CREDIT_NOTE

    const { data: resourcesList = [] } = useApiQuery(fetchTimelineResources, {
        queryName: ConfigQuery.TIMELINE_RESOURCES_FOR_USER,
        dependencies: {
            userId: mainUser.userId,
            resourceTypes: "ROOM",
        },
        select: transformTimeResourceData,
    })

    const list = (orderDetails.resources ?? []).flatMap((entry) => {
        const resource = resourcesList.find((res) => res.id === entry.id)
        if (!resource?.title) return []

        const setups =
            entry.tableSetups
                ?.map((selected) => {
                    const setupName = resource.tableSetups?.find((s) => s.key === selected.key)?.name
                    return setupName ? { name: setupName } : null
                })
                .filter((x): x is { name: string } => x !== null) ?? []

        return [setups.length > 0 ? { name: resource.title, tableSetups: setups } : { name: resource.title }]
    })

    return (
        <StandardDialog
            dialogTitle={ReceiptDialogTitle({ dialogType, orderId })}
            open={open}
            onClose={onClose}
            maxWidthProp="lg"
            scroll="body"
        >
            <DialogContent className={classes.dialogContent}>
                <Stack gap="2.5rem">
                    <Stack gap="1.5rem">
                        <Details
                            orderDetails={orderDetails}
                            mainUser={mainUser}
                            resourceNames={
                                resourcesList.find(
                                    (res) => res.id === +orderDetails[DynamicConfigKey.DELIVERY_LOCATIONS],
                                )?.title || ""
                            }
                        />
                        {orderDetails[DynamicConfigKey.RECURRENCY] && (
                            <RepeatOrder repeatDetails={orderDetails[DynamicConfigKey.RECURRENCY]} />
                        )}
                        {!!orderDetails[DynamicConfigKey.BUYER_PARTIES] && (
                            <BillReceiver
                                receiver={orderDetails[DynamicConfigKey.BUYER_PARTIES]}
                                costCodes={extractAccountingDimensionsWithNames(orderDetails)}
                                userId={mainUser.userId}
                            />
                        )}
                        {orderDetails[DynamicConfigKey.REGISTER_PARTICIPANTS] &&
                            !!orderDetails[DynamicConfigKey.REGISTER_PARTICIPANTS].length && (
                                <Attendees list={orderDetails[DynamicConfigKey.REGISTER_PARTICIPANTS]} />
                            )}
                    </Stack>
                    <Stack gap="2rem">
                        <Stack gap="1.25rem">
                            {!!orderDetails[DynamicConfigKey.MULTIPLE_DELIVERIES] && (
                                <OrderSummary
                                    deliveryDetails={orderDetails[DynamicConfigKey.MULTIPLE_DELIVERIES]}
                                    isRefundOrder={isRefundOrder}
                                />
                            )}

                            <DeliveryNote text={orderDetails[DynamicConfigKey.MERCHANT_ORDER_NOTE]} />
                        </Stack>

                        {!!list.length && <Resources resources={list} event={orderDetails["event"]} />}
                    </Stack>
                </Stack>
                <Stack gap="1rem" alignItems="center" justifyContent="center" flexDirection="row">
                    <LocalizedStrict id="confirm-order-dialog-back-button">
                        <StandardButton
                            onClick={onClose}
                            fullWidth={false}
                            color="white"
                            variant="contained"
                            updateDefaultClasses={cx(classes.button, classes.buttonLight)}
                            disableElevation={false}
                        >
                            Keep editing
                        </StandardButton>
                    </LocalizedStrict>
                    <LocalizedStrict id="confirm-order-dialog-next-button">
                        <StandardLoadingButton
                            onClick={handleConfirm}
                            loading={!!isSubmitting}
                            disabled={isSubmitting}
                            fullWidth={false}
                            updateDefaultClasses={classes.button}
                            disableElevation={false}
                        >
                            Confirm order
                        </StandardLoadingButton>
                    </LocalizedStrict>
                </Stack>
            </DialogContent>
        </StandardDialog>
    )
}
